import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';

interface AdvancedPerformanceOptimizerProps {
  preloadImages?: string[];
  prefetchUrls?: string[];
  dnsPreconnect?: string[];
  criticalResources?: { href: string; as: string; type?: string }[];
}

/**
 * Advanced Performance Optimizer Component
 * Implements Core Web Vitals optimization and resource loading strategies
 * Note: No external font loading - all fonts are self-hosted for DSGVO compliance
 */
const AdvancedPerformanceOptimizer: React.FC<AdvancedPerformanceOptimizerProps> = ({
  preloadImages = [],
  prefetchUrls = [],
  dnsPreconnect = [],
  criticalResources = []
}) => {
  useEffect(() => {
    // Initialize Intersection Observer for lazy loading optimization
    const observeImages = () => {
      const images = document.querySelectorAll('img[loading="lazy"]');
      if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target as HTMLImageElement;
              if ('decode' in img) {
                img.decode().then(() => {
                  img.style.opacity = '1';
                }).catch(() => {
                  img.style.opacity = '1';
                });
              }
            }
          });
        }, {
          rootMargin: '50px 0px'
        });
        
        images.forEach(img => imageObserver.observe(img));
      }
    };

    // Initialize optimizations
    observeImages();

    // Preload critical resources after initial render
    const timer = setTimeout(() => {
      criticalResources.forEach(resource => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = resource.href;
        link.as = resource.as;
        if (resource.type) link.type = resource.type;
        document.head.appendChild(link);
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [criticalResources]);

  return (
    <Helmet>
      {/* DNS Preconnect for allowed third-party domains (no Google Fonts) */}
      {dnsPreconnect
        .filter(domain => !domain.includes('fonts.googleapis') && !domain.includes('fonts.gstatic'))
        .map(domain => (
          <link key={domain} rel="preconnect" href={domain} crossOrigin="" />
        ))}

      {/* Critical images are preloaded in index.html - no duplicate preloads here */}

      {/* Prefetch next page resources */}
      {prefetchUrls.map(url => (
        <link key={url} rel="prefetch" href={url} />
      ))}

      {/* Core Web Vitals optimization hints */}
      <meta httpEquiv="Accept-CH" content="DPR, Viewport-Width, Width" />
      
      {/* Performance optimization meta tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="msapplication-tap-highlight" content="no" />
      
      {/* Service Worker registration hint */}
      <meta name="mobile-web-app-capable" content="yes" />
    </Helmet>
  );
};

export default AdvancedPerformanceOptimizer;
